import type { IndicatorInstance, AlligatorSettings, BollingerBandsSettings, DonchianChannelSettings, FractalSettings, FractalChaosBandsSettings, IchimokuKinkoHyoSettings, ParabolicSARSettings, SuperTrendSettings, ZigZagSettings } from "features/indicators/model";
import { INDICATOR_DEFAULT_SETTINGS } from "features/indicators/model";
import type { CandleWithFlag, ZoomRange } from "features/chart";
import { calcAlligator } from "../../model/calcAlligator";
import { drawAlligator } from "./drawAlligator";
import { calcBollingerBands } from "../../model/calcBollingerBands";
import { drawBollingerBands } from "./drawBollingerBands";
import { calcDonchianChannel } from "../../model/calcDonchianChannel";
import { drawDonchianChannel } from "./drawDonchianChannel";
import { calcFractal } from "../../model/calcFractal";
import { drawFractal } from "./drawFractal";
import { calcFractalChaosBands } from "../../model/calcFractalChaosBands";
import { drawFractalChaosBands } from "./drawFractalChaosBands";
import { calcIchimoku } from "../../model/calcIchimoku";
import { drawIchimoku } from "./drawIchimoku";
import { calcParabolicSAR } from "../../model/calcParabolicSAR";
import { drawParabolicSAR } from "./drawParabolicSAR";
import { calcSuperTrend } from "../../model/calcSuperTrend";
import { drawSuperTrend } from "./drawSuperTrend";
import { calcZigZag } from "../../model/calcZigZag";
import { drawZigZag } from "./drawZigZag";

export function renderOnChart(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  data: CandleWithFlag[],
  zoom: ZoomRange,
  instance: IndicatorInstance,
): void {
  const s = instance.settings ?? INDICATOR_DEFAULT_SETTINGS[instance.name];
  if (!s) return;

  if (instance.name === "alligator") {
    const al = s as AlligatorSettings;
    const lines = calcAlligator(
      data, al.jawsPeriod, al.jawsShift, al.teethPeriod, al.teethShift, al.lipsPeriod, al.lipsShift,
    );
    drawAlligator(ctx, width, height, data, zoom, lines, al);
  } else if (instance.name === "bollingerBands") {
    const bb = s as BollingerBandsSettings;
    const bands = calcBollingerBands(data, bb.period, bb.deviation);
    drawBollingerBands(ctx, width, height, data, zoom, bands, bb);
  } else if (instance.name === "donchianChannel") {
    const dc = s as DonchianChannelSettings;
    const lines = calcDonchianChannel(data, dc.period);
    drawDonchianChannel(ctx, width, height, data, zoom, lines, dc);
  } else if (instance.name === "fractal") {
    const fr = s as FractalSettings;
    const pts = calcFractal(data, fr.period);
    drawFractal(ctx, width, height, data, zoom, pts, fr);
  } else if (instance.name === "fractalChaosBands") {
    const fcb = s as FractalChaosBandsSettings;
    const lines = calcFractalChaosBands(data, fcb.period);
    drawFractalChaosBands(ctx, width, height, data, zoom, lines, fcb);
  } else if (instance.name === "ichimokuKinkoHyo") {
    const ich = s as IchimokuKinkoHyoSettings;
    const lines = calcIchimoku(data, ich.tenkanSenPeriod, ich.kijunSenPeriod, ich.senkouSpanBPeriod);
    drawIchimoku(ctx, width, height, data, zoom, lines, ich);
  } else if (instance.name === "parabolicSAR") {
    const sar = s as ParabolicSARSettings;
    const values = calcParabolicSAR(data, sar.acceleration, sar.maxAcceleration);
    drawParabolicSAR(ctx, width, height, data, zoom, values, sar);
  } else if (instance.name === "superTrend") {
    const st = s as SuperTrendSettings;
    const res = calcSuperTrend(data, st.atrPeriod, st.multiplier);
    drawSuperTrend(ctx, width, height, data, zoom, res, st);
  } else if (instance.name === "zigZag") {
    const zz = s as ZigZagSettings;
    const values = calcZigZag(data, zz.deviation, zz.depth, zz.backstep);
    drawZigZag(ctx, width, height, data, zoom, values, zz);
  }
}
